// ─────────────────────────────────────────────────────────────────────────────
// store/index.ts — Redux store configuration
//
// Uses Redux Toolkit's configureStore with redux-persist to survive page
// reloads.  Only theme and patterns (playground data) are persisted — cv data
// is always re-fetched so it stays fresh.
// ─────────────────────────────────────────────────────────────────────────────

import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // uses localStorage

import cvReducer       from './cvSlice';
import themeReducer    from './themeSlice';
import uiReducer       from './uiSlice';
import patternsReducer from './patternsSlice';

// ── Persist Config ─────────────────────────────────────────────────────────────
// Only persist theme preference and patterns playground data.
// cv data is always fetched fresh; ui state (menu open, etc.) resets on load.
const persistConfig = {
  key: 'jc-cv-root',
  storage,
  whitelist: ['theme', 'patterns', 'ui'], // 'cv' is intentionally excluded
};

// ── Root Reducer ───────────────────────────────────────────────────────────────
const rootReducer = combineReducers({
  cv:       cvReducer,
  theme:    themeReducer,
  ui:       uiReducer,
  patterns: patternsReducer,
});

// Wrap the root reducer with persist so selected slices are saved to localStorage
const persistedReducer = persistReducer(persistConfig, rootReducer);

// ── Store ──────────────────────────────────────────────────────────────────────
export const store = configureStore({
  reducer: persistedReducer,

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // redux-persist dispatches non-serialisable actions internally;
      // ignore them in the serializability check to avoid console warnings.
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),

  devTools: import.meta.env.DEV, // Enable Redux DevTools in development only
});

// ── Persistor ─────────────────────────────────────────────────────────────────
// Exported and passed to <PersistGate> in main.tsx so the app waits for
// localStorage to hydrate before rendering (prevents theme flash).
export const persistor = persistStore(store);

// ── TypeScript Helpers ────────────────────────────────────────────────────────
// RootState and AppDispatch are used throughout the app via typed hooks
export type RootState   = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
